import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  FiCheckCircle, FiShield, FiTarget, FiEye, FiArrowRight,
  FiUsers, FiGlobe, FiStar, FiZap, FiExternalLink,
} from 'react-icons/fi';
import { Link } from 'react-router-dom';

gsap.registerPlugin(ScrollTrigger);

/* ─── Reusable animated counter ───────────────────────────── */
const stats = [
  { value: '3+', label: 'Years in Business' },
  { value: '200+', label: 'Projects Delivered' },
  { value: '50+', label: 'Expert Team Members' },
  { value: '2k+', label: 'Happy Clients' },
];

const strengths = [
  'Strategic Development', 'Thorough Market Analysis',
  'Tailored Experiences', 'Dedicated Q/A Team',
  'Effortless Communication', 'Intuitive User Interface',
  'Optimal Platform Selection', 'Highly Functional Solutions',
  'Transparent Reporting', 'On-Time Delivery',
];

const values = [
  { icon: FiZap, title: 'Innovation', desc: 'We stay ahead of the curve, leveraging the latest tech stacks and design trends to deliver future-proof solutions.' },
  { icon: FiShield, title: 'Integrity', desc: 'Trust is earned. We operate with full transparency — every process, every cost, every milestone is shared with you.' },
  { icon: FiUsers, title: 'Collaboration', desc: 'Your team becomes our team. We work closely with stakeholders to align every decision with your business goals.' },
  { icon: FiStar, title: 'Excellence', desc: 'Mediocrity is not in our vocabulary. We hold every deliverable to the highest standard of quality and craftsmanship.' },
];

const AboutContent = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      /* hero grid */
      gsap.fromTo('.ab-hero-left',
        { x: -80, opacity: 0 },
        { x: 0, opacity: 1, duration: 1.1, ease: 'power3.out', scrollTrigger: { trigger: '.ab-hero-grid', start: 'top 75%' } }
      );
      gsap.fromTo('.ab-hero-right',
        { x: 80, opacity: 0 },
        { x: 0, opacity: 1, duration: 1.1, ease: 'power3.out', delay: 0.15, scrollTrigger: { trigger: '.ab-hero-grid', start: 'top 75%' } }
      );

      /* stats */
      gsap.fromTo('.ab-stat',
        { y: 50, opacity: 0, scale: 0.8 },
        { y: 0, opacity: 1, scale: 1, duration: 0.7, stagger: 0.12, ease: 'back.out(1.7)', scrollTrigger: { trigger: '.ab-stats-row', start: 'top 80%' } }
      );

      /* cert banner */
      gsap.fromTo('.ab-cert',
        { y: 60, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, ease: 'power3.out', scrollTrigger: { trigger: '.ab-cert', start: 'top 80%' } }
      );

      /* mission / vision */
      gsap.fromTo('.ab-mv-card',
        { y: 60, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.9, stagger: 0.2, ease: 'power3.out', scrollTrigger: { trigger: '.ab-mv-grid', start: 'top 78%' } }
      );

      /* values */
      gsap.fromTo('.ab-val-card',
        { y: 60, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, stagger: 0.15, ease: 'power3.out', scrollTrigger: { trigger: '.ab-values-grid', start: 'top 78%' } }
      );

      /* CTA strip */
      gsap.fromTo('.ab-cta-strip',
        { scale: 0.95, opacity: 0 },
        { scale: 1, opacity: 1, duration: 1, ease: 'power3.out', scrollTrigger: { trigger: '.ab-cta-strip', start: 'top 85%' } }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="py-28 px-6 bg-transparent overflow-hidden">
      <div className="max-w-7xl mx-auto space-y-36">

        {/* ═══════════════════════════════════════════════════════════
            1. HERO SPLIT — Image + Story
        ═══════════════════════════════════════════════════════════ */}
        <div className="ab-hero-grid grid lg:grid-cols-2 gap-20 items-center">

          {/* LEFT — Stacked visuals */}
          <div className="ab-hero-left relative">
            {/* Main image */}
            <div className="relative rounded-[2.5rem] overflow-hidden border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
              <img
                src="/about.jpeg"
                alt="RojrzTech Team"
                className="w-full h-[550px] object-cover"
              />
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            </div>

            {/* Floating Logo Badge */}
            <div className="absolute -bottom-8 -right-6 bg-white rounded-2xl p-5 shadow-2xl border border-white/20 w-36">
              <img src="/logo.png" alt="RojrzTech Logo" className="w-full object-contain" />
            </div>

            {/* Floating stat chip */}
            <div className="absolute top-6 -left-6 bg-black/70 backdrop-blur-xl border border-[#F2B135]/40 rounded-2xl px-5 py-4 shadow-xl flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#F2B135] flex items-center justify-center">
                <FiStar className="text-black" size={18} fill="currentColor" />
              </div>
              <div>
                <div className="text-white font-black text-lg leading-none">4.9/5</div>
                <div className="text-white/50 text-xs mt-0.5">Client Rating</div>
              </div>
            </div>

            {/* Floating years chip */}
            <div className="absolute bottom-20 -left-6 bg-[#F2B135] rounded-2xl px-5 py-3 shadow-xl">
              <div className="text-black font-black text-xl leading-none">3+</div>
              <div className="text-black/70 text-xs font-semibold mt-0.5">Years of Trust</div>
            </div>
          </div>

          {/* RIGHT — Text Content */}
          <div className="ab-hero-right space-y-8">
            <div className="inline-flex items-center gap-2 bg-[#F2B135]/10 border border-[#F2B135]/20 px-4 py-1.5 rounded-full mb-4">
              <span className="w-2 h-2 rounded-full bg-[#F2B135] animate-pulse" />
              <span className="text-xs font-black text-[#F2B135] uppercase tracking-[0.2em]">
                Who We Are
              </span>
            </div>

            <h2 className="text-5xl md:text-6xl font-black text-white leading-[1.08]">
              From Humble Beginnings to{' '}
              <span className="text-[#F2B135]">Global Impact</span>
            </h2>

            <p className="text-gray-300 text-lg leading-relaxed">
              Founded in 2023, RojrzTech was born with a singular mission — to bring world-class digital solutions to businesses of every size. Starting from scratch with passion and precision, we've grown into a globally trusted software and digital marketing agency.
            </p>
            <p className="text-gray-400 text-base leading-relaxed">
              We go beyond writing code. We craft digital ecosystems — from pixel-perfect UI/UX to high-performance backends — that help our clients outperform their competition and leave a lasting mark online.
            </p>

            {/* Checklist */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              {strengths.map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-white font-semibold text-sm group">
                  <FiCheckCircle className="text-[#F2B135] flex-shrink-0 transition-transform group-hover:scale-110" size={18} />
                  {item}
                </div>
              ))}
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link to="/contact" className="btn-animated-gold px-8 py-4 text-base">
                Start Your Project <FiArrowRight size={16} />
              </Link>
              <Link
                to="/portfolio"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/20 text-white font-bold text-sm hover:border-[#F2B135] hover:text-[#F2B135] transition-all duration-300"
              >
                <FiGlobe size={16} /> View Our Work
              </Link>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════
            2. STATS ROW
        ═══════════════════════════════════════════════════════════ */}
        <div className="ab-stats-row grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <div
              key={i}
              className="ab-stat group relative bg-[#0d0d0d] border border-white/8 hover:border-[#F2B135]/50 rounded-3xl p-8 text-center transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(242,177,53,0.1)]"
            >
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-[#F2B135]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="relative z-10">
                <div className="text-4xl md:text-5xl font-black text-[#F2B135] mb-2">{s.value}</div>
                <div className="text-white/60 text-sm font-semibold">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ═══════════════════════════════════════════════════════════
            3. SELLER CERTIFICATE / VERIFIED CREDIBILITY
        ═══════════════════════════════════════════════════════════ */}
        <div className="ab-cert relative overflow-hidden rounded-[2.5rem] border border-[#F2B135]/25 bg-gradient-to-r from-[#0d0d0d] via-[#111] to-[#0d0d0d] p-12 md:p-16 shadow-[0_0_60px_rgba(242,177,53,0.06)]">
          {/* BG glow */}
          <div className="absolute -top-20 -left-20 w-80 h-80 bg-[#F2B135]/8 rounded-full blur-[100px] pointer-events-none" />
          <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-[#F2B135]/5 rounded-full blur-[120px] pointer-events-none" />

          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-12">
            {/* Left info */}
            <div className="flex flex-col lg:flex-row items-center lg:items-start gap-8 text-center lg:text-left">
              {/* Shield icon */}
              <div className="flex-shrink-0 w-24 h-24 rounded-2xl bg-[#F2B135]/10 border border-[#F2B135]/30 flex items-center justify-center">
                <FiShield className="text-[#F2B135]" size={44} />
              </div>
              <div className="space-y-3">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-green-500/10 border border-green-500/30 text-green-400 text-xs font-bold uppercase tracking-widest">
                  <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" /> Verified & Registered
                </div>
                <h3 className="text-3xl md:text-4xl font-black text-white">
                  Officially Registered Business
                </h3>
                <p className="text-gray-400 text-base leading-relaxed max-w-xl">
                  RojrzTech is a fully registered and certified business entity. Our seller certificate is publicly verifiable through the Texas Comptroller's official portal. Transparency isn't just a value — it's our foundation.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <div className="flex items-center gap-2 text-white/60 text-sm">
                    <FiCheckCircle className="text-[#F2B135]" size={15} /> Texas Franchise Certificate
                  </div>
                  <div className="flex items-center gap-2 text-white/60 text-sm">
                    <FiCheckCircle className="text-[#F2B135]" size={15} /> Account No: 32092816522
                  </div>
                </div>
              </div>
            </div>

            {/* Right CTA */}
            <div className="flex flex-col items-center gap-4 flex-shrink-0">
              <a
                href="https://comptroller.texas.gov/taxes/franchise/account-status/search/32092816522"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-animated-gold px-8 py-4 text-base whitespace-nowrap"
              >
                <FiExternalLink size={16} /> Verify Certificate
              </a>
              <span className="text-white/30 text-xs font-medium">Opens Texas Comptroller Portal</span>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════
            4. MISSION & VISION (large immersive cards)
        ═══════════════════════════════════════════════════════════ */}
        <div className="ab-mv-grid grid md:grid-cols-2 gap-8">

          {/* Mission */}
          <div className="ab-mv-card group relative overflow-hidden rounded-[2rem] border border-white/8 bg-[#0d0d0d] p-12 hover:border-[#F2B135]/40 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_30px_70px_rgba(242,177,53,0.08)]">
            <div className="absolute inset-0 bg-gradient-to-br from-[#F2B135]/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative z-10 space-y-6">
              <div className="w-16 h-16 rounded-2xl bg-[#F2B135]/10 border border-[#F2B135]/30 flex items-center justify-center group-hover:bg-[#F2B135]/20 transition-all duration-300">
                <FiTarget className="text-[#F2B135]" size={32} />
              </div>
              <div>
                <div className="text-[#F2B135] text-xs font-bold uppercase tracking-widest mb-3">Our Purpose</div>
                <h4 className="text-4xl font-black text-white mb-5 leading-tight">Our Mission</h4>
                <p className="text-gray-400 text-lg leading-relaxed">
                  To empower businesses — from early-stage startups to enterprise giants — with intuitive, robust, and SEO-optimized digital experiences that drive measurable growth and long-term market success.
                </p>
              </div>
              <div className="space-y-3 pt-2">
                {['Build products that solve real problems', 'Prioritize user experience above all', 'Deliver measurable, data-driven results'].map((point, i) => (
                  <div key={i} className="flex items-center gap-3 text-white/70 text-sm font-medium">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#F2B135] flex-shrink-0" />
                    {point}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Vision */}
          <div className="ab-mv-card group relative overflow-hidden rounded-[2rem] border border-[#F2B135]/20 bg-gradient-to-br from-[#F2B135]/5 to-[#0d0d0d] p-12 hover:border-[#F2B135]/50 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_30px_70px_rgba(242,177,53,0.12)]">
            <div className="absolute top-0 right-0 w-60 h-60 bg-[#F2B135]/8 rounded-full blur-[80px] pointer-events-none" />
            <div className="relative z-10 space-y-6">
              <div className="w-16 h-16 rounded-2xl bg-[#F2B135]/20 border border-[#F2B135]/40 flex items-center justify-center group-hover:bg-[#F2B135]/30 transition-all duration-300">
                <FiEye className="text-[#F2B135]" size={32} />
              </div>
              <div>
                <div className="text-[#F2B135] text-xs font-bold uppercase tracking-widest mb-3">Our Direction</div>
                <h4 className="text-4xl font-black text-white mb-5 leading-tight">Our Vision</h4>
                <p className="text-gray-400 text-lg leading-relaxed">
                  To become the global benchmark for software innovation, design excellence, and client transparency — expanding as the world's most trusted digital partner for ambitious brands ready to lead their industries.
                </p>
              </div>
              <div className="space-y-3 pt-2">
                {['Lead with innovation, not imitation', 'Set the global standard in design', 'Grow 1,000 brands to market leadership'].map((point, i) => (
                  <div key={i} className="flex items-center gap-3 text-white/70 text-sm font-medium">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#F2B135] flex-shrink-0" />
                    {point}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════
            5. CORE VALUES GRID
        ═══════════════════════════════════════════════════════════ */}
        <div>
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 bg-[#F2B135]/10 border border-[#F2B135]/20 px-4 py-1.5 rounded-full mb-4">
              <span className="w-2 h-2 rounded-full bg-[#F2B135] animate-pulse" />
              <span className="text-xs font-black text-[#F2B135] uppercase tracking-[0.2em]">
                The Principles We Live By
              </span>
            </div>
            <h3 className="text-4xl md:text-5xl font-black text-white">Our Core Values</h3>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Everything we build, every decision we make, every team member we hire — all anchored to these four pillars.
            </p>
          </div>

          <div className="ab-values-grid grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <div
                key={i}
                className="ab-val-card group relative overflow-hidden rounded-2xl bg-[#0d0d0d] border border-white/8 p-8 hover:border-[#F2B135]/40 transition-all duration-400 hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(242,177,53,0.08)] text-center"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#F2B135]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400" />
                <div className="relative z-10 space-y-4">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-[#F2B135]/10 border border-[#F2B135]/25 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <v.icon className="text-[#F2B135]" size={26} />
                  </div>
                  <h5 className="text-xl font-black text-white">{v.title}</h5>
                  <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default AboutContent;